"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

export interface User {
  no: number;
  mgid: string;
  mgname: string;
  mgtype: number;
  mglevel: number;
  mgemail: string;
  comno: number;
  comname: string;
  comtype: number;
  roleText: string;
}

interface UseAuthReturn {
  user: User | null;
  loading: boolean;
  login: (mgid: string, mgpwd: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  // 사용자 세션 확인 - 성능 최적화
  const checkSession = async () => {
    try {
      // AbortController로 요청 타임아웃 설정
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3초 타임아웃

      const response = await fetch("/api/auth/me", {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn("세션 확인 타임아웃");
      } else {
        console.error("세션 확인 오류:", error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 로그인
  const login = async (mgid: string, mgpwd: string) => {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mgid, mgpwd }),
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("로그인 오류:", error);
      return { success: false, message: "네트워크 오류가 발생했습니다." };
    }
  };

  // 로그아웃
  const logout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
      });

      setUser(null);
      router.push("/login");
    } catch (error) {
      console.error("로그아웃 오류:", error);
      // 에러가 발생해도 로컬 상태는 초기화
      setUser(null);
      router.push("/login");
    }
  };

  // 세션 새로고침
  const refresh = async () => {
    await checkSession();
  };

  // 컴포넌트 마운트 시 세션 확인
  useEffect(() => {
    checkSession();
  }, []);

  return {
    user,
    loading,
    login,
    logout,
    refresh,
  };
}

// 권한 체크 헬퍼 함수들
export const useAuthHelpers = () => {
  const { user } = useAuth();

  return {
    isAuthenticated: () => user !== null,
    isHeadOffice: () => user?.mgtype === 9,
    isBranch: () => user?.mgtype === 3,
    isManager: () => {
      if (!user) return false;
      // 본사: 스탭 이상 (mglevel >= 11)
      if (user.mgtype === 9 && user.mglevel >= 11) return true;
      // 지사: 중간관리자 이상 (mglevel >= 5)
      if (user.mgtype === 3 && user.mglevel >= 5) return true;
      return false;
    },
    isSuperAdmin: () => {
      if (!user) return false;
      // 본사 최고관리자 또는 지사 대표관리자
      return (user.mgtype === 9 && user.mglevel >= 20) || 
             (user.mgtype === 3 && user.mglevel >= 9);
    },
    hasPermission: (requiredType?: number, requiredLevel?: number) => {
      if (!user) return false;

      // 본사 최고관리자는 모든 권한 허용
      if (user.mgtype === 9 && user.mglevel >= 20) return true;

      // 특정 타입이 요구되는 경우
      if (requiredType !== undefined && user.mgtype !== requiredType) return false;

      // 특정 레벨이 요구되는 경우
      if (requiredLevel !== undefined && user.mglevel < requiredLevel) return false;

      return true;
    },
  };
}; 